import type {
  Flow_AbstractComponent,
  Flow_Diff,
  Flow_ComponentProps,
  Flow_ObjMap,
} from 'src/types/flow-to-typescript-codemod';
import type {
  CUSTOM_FIELDS,
  CustomForm as CustomFormType,
  JOB_CUSTOM_FIELD_INFO,
} from 'src/types/crm';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {Formik, Form} from 'formik';
import {DynamicField} from './dynamic-field';
import {
  makeConditional,
  getFieldsWithConditions,
  generateInitialValues,
  generateValidator,
  computeInitialValues,
  transformCustomFields,
} from 'src/components/crm/jobs/crud/job-details/custom-form/helpers';
import classify from 'src/utils/classify';
import css from './custom-form.css';


export type FieldClassNames = Readonly<{
  textInput?: string;
  numberRangeContainer?: string;
  textAreaInput?: string;
  dropdownInput?: string;
  booleanInput?: string;
  fileUpload?: string;
}>;

export type ClassNames = Readonly<{
  fieldsContainer?: string;
  form?: string;
  submitButton?: string;
  fields?: FieldClassNames;
}>;

type CustomFormProps = {
  // Use any type for fields to handle both schema formats
  fields: Array<CustomFormType> | Array<JOB_CUSTOM_FIELD_INFO> | null;
  onChange: (
    values: Array<{
      fieldId: number;
      value: string | null;
      fieldType?: string;
    }>,
    isValid: boolean,
    errors: Array<{
      fieldId: number;
      error: string;
    }>,
  ) => void;
  initialValues: CUSTOM_FIELDS[];
  onSubmit?: (arg1: unknown) => void;
  submitButtonText?: string;
  showSubmitButton?: boolean;
  classNames?: ClassNames;
  viewType?: string;
  showFormTypes?: Array<string>;
  returnAllValues?: boolean; // Whether to return all values or only changed values;
  checkCondition?: boolean;
  required?: boolean;
  dateRangePlacement?: string;
  isCustomFilter?: boolean;
};

const CustomForm: Flow_AbstractComponent<CustomFormProps, unknown> =
  React.memo<CustomFormProps>(
    ({
      fields,
      onChange,
      initialValues = {},
      onSubmit,
      submitButtonText = 'Submit',
      showSubmitButton = false,
      classNames,
      viewType = '2', // 1 - show in one column , 2 - show in two columns
      showFormTypes = [],
      returnAllValues = false, // default to only return changed values
      checkCondition = true,
      required = true,
      dateRangePlacement = 'bottom-start',
      isCustomFilter = false,
    }) => {
      const {t, makeKey,language} = useLabelTranslation();
      const lastValuesRef = React.useRef(null);
      const [initialFormValues, setInitialFormValues] = React.useState({});
      const isInitializedRef = React.useRef(false);

      // Check the schema format and normalize the fields structure
      const normalizedFields = React.useMemo(() => {
        if (!fields || fields.length === 0) {
          return null;
        }

        // Check if it's the original schema format
        // @ts-expect-error - TS2339 - Property 'fields' does not exist on type 'JOB_CUSTOM_FIELD_INFO | CustomForm'.
        if (fields[0].fields) {
          // @ts-expect-error - TS2339 - Property 'fields' does not exist on type 'JOB_CUSTOM_FIELD_INFO | CustomForm'.
          return fields[0].fields;
        }

        // Check if it's the alternative schema format
        // if (fields[0].fieldType) {
        //   // Convert the alternative schema to the expected format
        //   return fields.map((field) => ({
        //     customFieldId: field.id,
        //     questionTag: field.name,
        //     show: 'OPTIONAL',
        //     customField: field,
        //     locked: false,
        //     editable: !field.disabled,
        //     // Add required properties with default values
        //     causeVersionChange: false,
        //     parentFieldId: 0,
        //     offerLetterFieldId: 0,
        //     canApproverView: false,
        //     canAgencyView: false,
        //     canReferralView: false,
        //     canInternalJobPortalView: false,
        //     value: null,
        //     valueId: 0,
        //   }));
        // }

        return null;
      }, [fields]);

      const formattedInitialValues = React.useMemo(() => {
        if (Array.isArray(initialValues)) {
          return initialValues.reduce<Record<string, any>>((acc, field) => {
            const fieldName = `custom_${field.id}`;
            const fieldType = field.fieldType;
            acc[fieldName] = computeInitialValues(fieldType, field);
            return acc;
          }, {});
        }
        return initialValues;
      }, [initialValues]);

      const hasInitialValues = React.useMemo(
        () =>
          Object.values(formattedInitialValues).filter(
            (value) => value !== null || value !== undefined || value !== '',
          ).length > 0,
        [formattedInitialValues],
      );

      // Generate proper initial values for Formik
      const formikInitialValues = React.useMemo(() => {
        if (!normalizedFields) {
          return {};
        }
        return generateInitialValues(normalizedFields, formattedInitialValues);
      }, [normalizedFields, formattedInitialValues]);

      // Initialize initialFormValues once when form is ready
      React.useEffect(() => {
        if (!isInitializedRef.current && normalizedFields) {
          const initialVals = generateInitialValues(
            normalizedFields,
            formattedInitialValues,
          );
          setInitialFormValues(initialVals);
          isInitializedRef.current = true;
        }
      }, [normalizedFields, formattedInitialValues]);

      if (!normalizedFields) {
        return null;
      }

      // Handle form submission
      // @ts-expect-error - TS7031 - Binding element 'setSubmitting' implicitly has an 'any' type.
      const handleSubmit = (values: any, {setSubmitting}) => {
        const formattedValues: Record<string, any> = {};

        if (onSubmit) {
          onSubmit(formattedValues);
        }

        setSubmitting(false);
      };

      // Get the ID for the dynamic form container
      const formId = fields && fields.length > 0 ? fields[0].id || '' : '';

      // Helper function to track changed fields
      const getChangedFields = (
        currentValues: any,
        initialVals: Record<any, any>,
      ) => {
        const changedFields: Array<never> = [];

        Object.keys(currentValues).forEach((key) => {
          if (key.startsWith('custom_')) {
            const currentValue = currentValues[key];
            const initialValue = initialVals[key];

            // Check if the value has changed
            const hasChanged =
              (initialValue === undefined && currentValue !== undefined) ||
              (initialValue !== undefined &&
                JSON.stringify(currentValue) !== JSON.stringify(initialValue));

            if (hasChanged) {
              // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
              changedFields.push(key);
            }
          }
        });

        return changedFields;
      };

      // Handle onChange separate from render cycle to prevent infinite loops
      // React.useCallback(
      const handleFormChange = (
        values: any,
        isValid: any,
        isDirty: any,
        errors: any,
      ) => {
        if (!onChange) {
          return;
        }
        // Determine which values to return based on the returnAllValues prop
        let transformedValues;

        if (returnAllValues) {
          // Return all values
          transformedValues = transformCustomFields(values, normalizedFields);
        } else {
          // Return only changed values
          const changedFieldKeys = getChangedFields(values, initialFormValues);
          const changedValues: Record<string, any> = {};

          changedFieldKeys.forEach((key) => {
            changedValues[key] = values[key];
          });

          transformedValues = transformCustomFields(
            changedValues,
            normalizedFields,
          );
        }

        // Skip if values haven't changed
        const valuesJson =
          JSON.stringify(transformedValues) + JSON.stringify(errors);
        if (lastValuesRef.current === valuesJson) {
          return;
        }

        // Update last values
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null'.
        lastValuesRef.current = valuesJson;

        // Call parent onChange
        onChange(
          transformedValues,
          hasInitialValues ? isValid : isValid && isDirty,
          errors,
        );
      };
      // [onChange, returnAllValues, normalizedFields, initialFormValues, hasInitialValues]);

      // Initialize form values once
      // React.useEffect(() => {
      //   if (!isInitializedRef.current) {
      //     setInitialFormValues(generateInitialValues(normalizedFields, formattedInitialValues));
      //     isInitializedRef.current = true;
      //   }
      // }, [ formattedInitialValues]);

      // // Handle form changes
      // const handleFormChangeEffect = React.useCallback((values, isValid, isDirty, errors) => {
      //   // Transform errors object to array format expected by onChange
      //   const errorArray = Object.entries(errors).map(([key, message]) => ({
      //     fieldId: parseInt(key.replace('custom_', ''), 10),
      //     error: String(message),
      //   }));

      //   handleFormChange(values, isValid, isDirty, errorArray);
      // }, [handleFormChange]);

      // React.useEffect(() => {
      //   if (!isInitializedRef.current) return;
      //   handleFormChangeEffect(
      //     generateInitialValues(normalizedFields, formattedInitialValues),
      //     true,
      //     false,
      //     {}
      //   );
      // }, [normalizedFields, formattedInitialValues, handleFormChangeEffect]);

      return (
        <div
          id={`dynamic-form-${formId}`}
          className={css.dynamicFieldContainer}
        >
          <Formik
            initialValues={formikInitialValues}
            onSubmit={handleSubmit}
            validate={generateValidator(normalizedFields)}
            validateOnChange
            validateOnBlur
            enableReinitialize={isCustomFilter}
            validateOnMount={hasInitialValues}
          >
            {(formikProps) => {
              const {
                handleSubmit,
                values,
                isSubmitting,
                setFieldValue,
                errors,
                isValid,
                dirty: isDirty, // Formik uses 'dirty' not 'isDirty'
              } = formikProps;

              // Create conditional field value setter
              const conditionalSetFieldValue = makeConditional(
                setFieldValue,
                normalizedFields,
              );

              // Get fields that should be displayed based on conditions
              const visibleFields = getFieldsWithConditions(
                values,
                normalizedFields,
                checkCondition,
              );

              // Remove the useLayoutEffect from here
              handleFormChange(values, isValid, isDirty, errors);

              return (
                // @ts-expect-error - TS2786 - 'Form' cannot be used as a JSX component.
                <Form
                  onSubmit={handleSubmit}
                  className={classify(css.form, classNames?.form)}
                >
                  <div
                    className={classify(
                      css.fieldsContainer,
                      viewType === '1' && css.oneColumn,
                      viewType === '2' && css.twoColumn,
                      classNames?.fieldsContainer,
                    )}
                  >
                    {visibleFields.map((field) => (
                      <DynamicField
                        key={`custom_${field?.customFieldId || field?.id}`}
                        field={field}
                        formikProps={{
                          ...formikProps,
                          setFieldValue: conditionalSetFieldValue,
                        }}
                        hasInitialValues={hasInitialValues}
                        classNames={classNames?.fields}
                        showFormTypes={showFormTypes}
                        required={required}
                        dateRangePlacement={dateRangePlacement}
                        isCustomFilter={isCustomFilter}
                      />
                    ))}
                  </div>
                  {showSubmitButton && (
                    <div className="form-actions">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="submit-button"
                      >
                        {t(makeKey(submitButtonText), submitButtonText)}
                      </button>
                    </div>
                  )}
                </Form>
              );
            }}
          </Formik>
        </div>
      );
    },
  );

export default CustomForm;
